/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

import React, {
  useMemo,
  useRef,
  useState,
  useCallback,
  useEffect,
} from 'react';
import {
  NativeScrollEvent,
  NativeSyntheticEvent,
  View,
  FlatList,
  Image,
  TouchableOpacity,
  LayoutChangeEvent,
  ImageSourcePropType,
} from 'react-native';
import { StyleSheet } from 'react-native-unistyles';
import IconView from '@airasia-phoenix/react-native/components/IconView';
import ImageIndicatorView, {
  ImageIndicatorViewStyle,
} from '@airasia-phoenix/react-native/components/ImageIndicatorView';
import { pd } from '@airasia-phoenix/react-native/utils/utils';
import { useDeviceInfo } from '@airasia-phoenix/react-native/utils';

import type { HotelBannerViewProps, HotelBannerItem } from '../../types';
import {
  getResponsiveBannerHeight,
  getResponsiveListingBannerHeight,
  MAX_BANNER_HEIGHT,
} from '../../utils/hotelBannerHeight';

const DEFAULT_AUTO_SCROLL_INTERVAL = 3000;

const isRemoteUrl = (url: HotelBannerItem['url']): url is string =>
  typeof url === 'string';

/**
 * HotelBanner - Presentation carousel component for banners
 */
const HotelBannerView = ({
  banners,
  testID = 'hotel-banner',
  autoScrollInterval = DEFAULT_AUTO_SCROLL_INTERVAL,
  onBannerPress,
  onBannerView,
  onBannerImageError,
  imageBorderRadius,
  displayVariant = 'default',
}: HotelBannerViewProps) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [containerWidth, setContainerWidth] = useState(0);
  const flatListRef = useRef<FlatList<HotelBannerItem> | null>(null);
  const viewedIndexesRef = useRef<Set<number>>(new Set());
  const { width: screenWidth } = useDeviceInfo();
  const effectiveWidth = containerWidth || screenWidth;
  const bannerHeight =
    displayVariant === 'listing'
      ? getResponsiveListingBannerHeight(effectiveWidth)
      : getResponsiveBannerHeight(effectiveWidth);

  // Measure the actual available width of the component
  const onLayout = useCallback((event: LayoutChangeEvent) => {
    const { width } = event.nativeEvent.layout;
    setContainerWidth(width);
  }, []);

  const numDots = useMemo(() => Math.max(1, banners.length), [banners.length]);

  const selectedImageIndex = useMemo(
    () => Math.max(0, Math.min(activeIndex, banners.length - 1)),
    [activeIndex, banners.length],
  );
  const pointerEvents = banners.every((banner) => !banner.redirectionUrl)
    ? 'none'
    : 'auto';

  const bannerSources = useMemo<ImageSourcePropType[]>(
    () =>
      banners.map((banner) =>
        isRemoteUrl(banner.url) ? { uri: banner.url } : banner.url,
      ),
    [banners],
  );

  const timerRef = useRef<number | null>(null);

  const notifyBannerView = useCallback(
    (index: number) => {
      if (!onBannerView) return;
      if (index < 0 || index >= banners.length) return;
      if (viewedIndexesRef.current.has(index)) return;
      viewedIndexesRef.current.add(index);
      onBannerView(banners[index], index);
    },
    [banners, onBannerView],
  );

  useEffect(() => {
    viewedIndexesRef.current.clear();
    if (banners.length > 0) {
      notifyBannerView(0);
    }
  }, [banners, notifyBannerView]);

  const scrollToNext = useCallback(() => {
    setActiveIndex((prev) => {
      const next = (prev + 1) % banners.length;
      flatListRef.current?.scrollToIndex({ index: next, animated: true });
      notifyBannerView(next);
      return next;
    });
  }, [banners.length, notifyBannerView]);

  useEffect(() => {
    if (banners.length <= 1) return;
    timerRef.current = setInterval(
      scrollToNext,
      autoScrollInterval,
    ) as unknown as number;
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [banners.length, scrollToNext, autoScrollInterval]);

  const handleBannerPress = useCallback(
    (item: HotelBannerItem, index: number) => {
      onBannerPress?.(item, index);
    },
    [onBannerPress],
  );

  const onMomentumScrollEnd = useCallback(
    (event: NativeSyntheticEvent<NativeScrollEvent>) => {
      const offsetX = event.nativeEvent.contentOffset.x;
      // Calculate index based on containerWidth, not screenWidth
      const newIndex = Math.round(offsetX / (containerWidth || 1));
      if (newIndex >= 0 && newIndex < banners.length) {
        setActiveIndex(newIndex);
        notifyBannerView(newIndex);
        if (banners.length > 1) {
          if (timerRef.current) clearInterval(timerRef.current);
          timerRef.current = setInterval(
            scrollToNext,
            autoScrollInterval,
          ) as unknown as number;
        }
      }
    },
    [
      containerWidth,
      banners.length,
      scrollToNext,
      autoScrollInterval,
      notifyBannerView,
    ],
  );

  const getItemLayout = useCallback(
    (_: unknown, index: number) => ({
      length: containerWidth,
      offset: containerWidth * index,
      index,
    }),
    [containerWidth],
  );

  const renderItem = useCallback(
    ({ item, index }: { item: HotelBannerItem; index: number }) => {
      const urlString = isRemoteUrl(item.url) ? item.url : undefined;
      const isSvg = Boolean(urlString?.toLowerCase().endsWith('.svg'));
      const source = bannerSources[index];
      const itemBannerHeight =
        displayVariant === 'listing' && item.height
          ? item.height
          : bannerHeight;
      const imageStyle = {
        height: itemBannerHeight,
        maxHeight: MAX_BANNER_HEIGHT,
        width: containerWidth,
        borderRadius: imageBorderRadius || 0,
        overflow: 'hidden' as const,
      };

      const content = isSvg ? (
        <IconView
          testID={`${testID}-image-${index}`}
          url={urlString}
          height={itemBannerHeight}
          width={containerWidth}
          style={imageStyle}
        />
      ) : (
        <Image
          testID={`${testID}-image-${index}`}
          source={source}
          style={[styles.bannerImage, imageStyle]}
          resizeMode="stretch"
          onError={() => onBannerImageError?.(item, index)}
        />
      );

      return (
        <View style={[styles.itemContainer, { width: containerWidth }]}>
          {item.redirectionUrl ? (
            <TouchableOpacity
              onPress={() => handleBannerPress(item, index)}
              style={{ width: containerWidth }}
              activeOpacity={0.9}
              delayPressIn={100}
            >
              {content}
            </TouchableOpacity>
          ) : (
            content
          )}
        </View>
      );
    },
    [
      bannerSources,
      bannerHeight,
      containerWidth,
      displayVariant,
      handleBannerPress,
      imageBorderRadius,
      onBannerImageError,
      styles,
      testID,
    ],
  );

  if (banners.length === 0) {
    return null;
  }

  return (
    <View
      testID={testID}
      style={styles.container}
      onLayout={onLayout}
      pointerEvents={pointerEvents}
    >
      <FlatList
        ref={flatListRef}
        data={banners}
        horizontal
        scrollEnabled={false}
        pagingEnabled
        showsHorizontalScrollIndicator={false}
        initialNumToRender={banners.length}
        maxToRenderPerBatch={banners.length}
        windowSize={Math.max(1, banners.length)}
        removeClippedSubviews={false}
        keyExtractor={(item) => item.id}
        renderItem={renderItem}
        onMomentumScrollEnd={onMomentumScrollEnd}
        scrollEventThrottle={16}
        getItemLayout={getItemLayout}
      />
      {banners.length > 1 && (
        <View testID={`${testID}-dots`} style={styles.pagination}>
          <ImageIndicatorView
            numDots={numDots}
            style={ImageIndicatorViewStyle.STANDARD}
            selectedIndex={selectedImageIndex}
          />
        </View>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    width: '100%',
  },
  itemContainer: {
    alignItems: 'center',
  },
  bannerImage: {
    borderRadius: 0,
  },
  pagination: {
    flexDirection: 'row',
    position: 'absolute',
    bottom: pd.ref.space.spacing50.px,
    left: 0,
    right: 0,
    alignItems: 'center',
    justifyContent: 'center',
  },
});

export default HotelBannerView;
